import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider as StyledThemeProvider, DefaultTheme } from "styled-components";
import {
  Button,
  Space,
  Col,
  Statistic,
  message,
  Typography,
  Modal,
  Tag,
  ConfigProvider,
} from "antd";
import {
  ReloadOutlined,
  PlusOutlined,
  TagOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PauseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import PromotionsTable from "./PromotionsTable";
import PromotionModal from "./PromotionModal";
import PromotionDetailsModal from "./PromotionDetailsModal";
import {
  Container,
  StyledCard,
  StatsRow,
  StatCard,
  HeaderSection,
  TopControlBar,
  CreateButton,
} from "./Styles";
import { mapBackendToFormData } from "./store/promotionsSlice";
import CustomSelector from "../../commonComponents/Selector";
import CustomInput from "../../commonComponents/input";
import { getAntdThemeConfig } from "../../utils/antdTheme";
import {
  Promotion,
  PromotionFormData,
  PromotionsProps,
} from "./types";
import {
  STATUS_FILTER_OPTIONS,
  TYPE_FILTER_OPTIONS,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_STATUS_FILTER,
  DEFAULT_TYPE_FILTER,
} from "./constants";
import { useTheme } from "../../context/ThemeContext";
import {
  fetchMerchants,
  fetchPromotions,
  fetchPromotion,
  fetchPromotionStats,
  createPromotion,
  updatePromotion,
  deletePromotion,
  clearSelectedPromotion,
  setSelectedMerchantId,
} from "./store/promotionsSlice";
import type { AppDispatch, RootState } from "../../redux/store";

const { Title } = Typography;
const { confirm } = Modal;

const Promotions: React.FC<PromotionsProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    promotions,
    merchants,
    selectedMerchantId,
    stats,
    loading,
    loadingMerchants,
    loadingDetails,
    saving,
  } = useSelector((state: RootState) => state.promotions);

  const [filteredPromotions, setFilteredPromotions] = useState<Promotion[]>([]);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>(DEFAULT_STATUS_FILTER);
  const [typeFilter, setTypeFilter] = useState<string>(DEFAULT_TYPE_FILTER);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);

  const [formData, setFormData] = useState<PromotionFormData>({
    title: "",
    description: "",
    code: "",
    type: "percentage",
    discountValue: 0,
    minOrderAmount: 0,
    maxDiscount: 0,
    startDate: "",
    endDate: "",
    isActive: true,
    usageLimit: 0,
    usageCount: 0,
  });

  const lastFetchedFilters = useRef<string | null>(null);
  const isFetching = useRef<boolean>(false);
  const { isDarkMode } = useTheme();

  const handleFetchPromotions = React.useCallback(async () => {
    if (!selectedMerchantId || isFetching.current || loading) return;
    
    isFetching.current = true;
    try {
      const result = await dispatch(fetchPromotions({
        merchantId: selectedMerchantId,
        filters: { status: statusFilter, type: typeFilter },
      }));
      
      if (fetchPromotions.rejected.match(result)) {
        const errorMessage = typeof result.payload === 'string' ? result.payload : "Failed to fetch promotions. Please try again.";
        message.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error fetching promotions:", error);
      message.error("Failed to fetch promotions. Please try again.");
    } finally {
      isFetching.current = false;
    }
  }, [dispatch, selectedMerchantId, statusFilter, typeFilter, loading]);

  const handleFetchStats = React.useCallback(async () => {
    if (!selectedMerchantId) return;
    
    try {
      const result = await dispatch(fetchPromotionStats(selectedMerchantId));
      if (fetchPromotionStats.rejected.match(result)) {
        console.error("Failed to fetch stats");
      }
    } catch (error: any) {
      console.error("Error fetching stats:", error);
    }
  }, [dispatch, selectedMerchantId]);

  // Fetch merchants on mount
  useEffect(() => {
    if (merchants.length === 0 && !loadingMerchants) {
      dispatch(fetchMerchants());
    }
  }, [dispatch, merchants.length, loadingMerchants]);

  // Fetch promotions and stats when filters change
  useEffect(() => {
    if (!selectedMerchantId || merchants.length === 0) return;
    
    const filterKey = JSON.stringify({ merchantId: selectedMerchantId, statusFilter, typeFilter });
    if (lastFetchedFilters.current === filterKey || isFetching.current || loading) {
      return;
    }

    lastFetchedFilters.current = filterKey;
    handleFetchPromotions();
    handleFetchStats();
  }, [selectedMerchantId, statusFilter, typeFilter, merchants.length, loading, handleFetchPromotions, handleFetchStats]);

  useEffect(() => {
    let filtered = [...promotions];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (promo) =>
          promo.title?.toLowerCase().includes(query)
      );
    }

    setFilteredPromotions(filtered);
    setCurrentPage(1);
  }, [promotions, searchQuery]);


  const generateCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  const handleCreate = () => {
    dispatch(clearSelectedPromotion());
    setSelectedPromotion(null);
    setFormData({
      title: "",
      description: "",
      code: "",
      type: "percentage",
      discountValue: 0,
      minOrderAmount: 0,
      maxDiscount: 0,
      startDate: "",
      endDate: "",
      isActive: true,
      usageLimit: 0,
      usageCount: 0,
    });
    setIsModalOpen(true);
  };

  const handleEdit = async (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    setLoadingModal(true);
    setIsModalOpen(true);

    try {
      if (selectedMerchantId && promotion.id) {
        const result = await dispatch(fetchPromotion({
          promotionId: promotion.id,
          merchantId: selectedMerchantId,
        }));
        
        if (fetchPromotion.fulfilled.match(result)) {
          const mappedData = mapBackendToFormData(result.payload);
          setFormData(mappedData);
        } else {
          // Fallback to using promotion data directly
          setFormData({
            title: promotion.title || "",
            description: promotion.description || "",
            code: promotion.code || promotion.promoCode || "",
            type: promotion.type || "percentage",
            discountValue: promotion.discountValue || 0,
            minOrderAmount: promotion.minOrderAmount || 0,
            maxDiscount: promotion.maxDiscount || 0,
            startDate: promotion.startDate || "",
            endDate: promotion.endDate || "",
            isActive: promotion.isActive !== undefined ? promotion.isActive : true,
            usageLimit: promotion.usageLimit || 0,
            usageCount: promotion.usageCount || 0,
          });
        }
      } else {
        setFormData({
          title: promotion.title || "",
          description: promotion.description || "",
          code: promotion.code || promotion.promoCode || "",
          type: promotion.type || "percentage",
          discountValue: promotion.discountValue || 0,
          minOrderAmount: promotion.minOrderAmount || 0,
          maxDiscount: promotion.maxDiscount || 0,
          startDate: promotion.startDate || "",
          endDate: promotion.endDate || "",
          isActive: promotion.isActive !== undefined ? promotion.isActive : true,
          usageLimit: promotion.usageLimit || 0,
          usageCount: promotion.usageCount || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching promotion details:", error);
      message.error("Failed to load promotion details.");
    } finally {
      setLoadingModal(false);
    }
  };

  const handleSave = async () => {
    if (!selectedMerchantId) {
      message.error("Merchant ID is missing. Please refresh the page.");
      return;
    }

    if (!formData.title) {
      message.error("Please fill in all required fields.");
      return;
    }

    if (!formData.startDate || !formData.endDate) {
      message.error("Please select both start and end dates.");
      return;
    }

    try {
      let result;
      if (selectedPromotion?.id) {
        result = await dispatch(updatePromotion({
          promotionId: selectedPromotion.id,
          formData,
          merchantId: selectedMerchantId,
        }));
      } else {
        result = await dispatch(createPromotion({
          formData,
          merchantId: selectedMerchantId,
        }));
      }

      if (updatePromotion.fulfilled.match(result) || createPromotion.fulfilled.match(result)) {
        message.success(`Promotion ${selectedPromotion ? "updated" : "created"} successfully.`);
        setIsModalOpen(false);
        handleFetchPromotions();
        handleFetchStats();
      } else {
        const errorMessage = typeof (result as any).payload === 'string' ? (result as any).payload : `Failed to ${selectedPromotion ? "update" : "create"} promotion.`;
        message.error(errorMessage);
      }
    } catch (error: any) {
      console.error("Error saving promotion:", error);
      message.error(`Failed to ${selectedPromotion ? "update" : "create"} promotion.`);
    }
  };

  const handleDelete = (promotion: Promotion) => {
    if (!selectedMerchantId || !promotion.id) {
      message.error("Missing required information to delete promotion.");
      return;
    }

    confirm({
      title: "Delete Promotion",
      content: `Are you sure you want to delete "${promotion.title}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const result = await dispatch(deletePromotion({
            promotionId: promotion.id!,
            merchantId: selectedMerchantId,
          }));

          if (deletePromotion.fulfilled.match(result as any)) {
            message.success("Promotion deleted successfully.");
            handleFetchPromotions();
            handleFetchStats();
          } else if (deletePromotion.rejected.match(result as any)) {
            const errorMessage = typeof (result as any).payload === 'string' ? (result as any).payload : "Failed to delete promotion.";
            message.error(errorMessage);
          }
        } catch (error: any) {
          console.error("Error deleting promotion:", error);
          message.error("Failed to delete promotion.");
        }
      },
    });
  };

  const handleViewDetails = (promotion: Promotion) => {
    setSelectedPromotion(promotion);
    setIsDetailOpen(true);
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    message.success(`Code "${code}" copied to clipboard.`);
  };

  const getStatusBadge = (promotion: Promotion): React.ReactNode => {
    const now = new Date();
    const endDate = promotion.endDate ? new Date(promotion.endDate) : null;
    const isExpired = endDate && endDate < now;
    
    const status = promotion.status || (promotion.isActive ? "active" : "draft");

    if (isExpired || status === "expired") {
      return <Tag color="default">Expired</Tag>;
    }
    if (status === "active") {
      return <Tag color="success">Active</Tag>;
    }
    if (status === "paused") {
      return <Tag color="warning">Paused</Tag>;
    }
    if (status === "cancelled") {
      return <Tag color="error">Cancelled</Tag>;
    }
    return <Tag color="orange">Draft</Tag>;
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case "percentage":
        return "Percentage";
      case "fixed":
        return "Fixed Amount";
      case "buy_one_get_one":
        return "BOGO";
      default:
        return type;
    }
  };

  const paginatedPromotions = filteredPromotions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const themeMode: 'light' | 'dark' = isDarkMode ? "dark" : "light";
  const styledTheme = { mode: themeMode } as DefaultTheme;

  return (
    <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
      <StyledThemeProvider theme={styledTheme}>
        <Container>
      <HeaderSection>
        <Space>
          <Title level={2} style={{ margin: 0 }}>
            Promotions Management
          </Title>
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleFetchPromotions}
            loading={loading}
          >
            Refresh
          </Button>
          <CreateButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Create Promotion
          </CreateButton>
        </Space>
      </HeaderSection>

      {/* Statistics Cards */}
      <StatsRow gutter={[12, 16]}>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="total">
            <Statistic
              title="Total Promotions"
              value={stats.total}
              valueStyle={{ color: "#722ed1" }}
              prefix={<TagOutlined style={{ color: "#722ed1" }} />}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="active">
            <Statistic
              title="Active"
              value={stats.active}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="draft">
            <Statistic
              title="Draft"
              value={stats.draft}
              valueStyle={{ color: "#fa8c16" }}
              prefix={<ExclamationCircleOutlined style={{ color: "#fa8c16" }} />}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="paused">
            <Statistic
              title="Paused"
              value={stats.paused}
              valueStyle={{ color: "#faad14" }}
              prefix={<PauseCircleOutlined style={{ color: "#faad14" }} />}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="expired">
            <Statistic
              title="Expired"
              value={stats.expired}
              valueStyle={{ color: "#8c8c8c" }}
              prefix={<ClockCircleOutlined style={{ color: "#8c8c8c" }} />}
            />
          </StatCard>
        </Col>
        <Col xs={24} sm={12} md={8} flex="1" style={{ minWidth: 0, display: 'flex' }}>
          <StatCard cardType="cancelled">
            <Statistic
              title="Cancelled"
              value={stats.cancelled}
              valueStyle={{ color: "#cf1322" }}
              prefix={<CloseCircleOutlined style={{ color: "#cf1322" }} />}
            />
          </StatCard>
        </Col>
      </StatsRow>

      {/* Filters and Search */}
      <StyledCard>
        <TopControlBar>
          <CustomInput
            placeholder="Search by title..."
            value={searchQuery}
            onChange={(value: string) => setSearchQuery(value)}
            allowClear
            width="300px"
          />
          <Space>
            <CustomSelector
              placeholder="Merchants"
              value={selectedMerchantId?.toString() || ""}
              onChange={(value: string) => {
                const merchantId = value ? parseInt(value) : null;
                dispatch(setSelectedMerchantId(merchantId));
              }}
              options={
                loadingMerchants
                  ? [{ value: "", label: "Loading..." }]
                  : merchants.length === 0
                  ? [{ value: "", label: "No merchants" }]
                  : merchants.map((merchant) => ({
                      value: merchant.id.toString(),
                      label: merchant.name || merchant.email || `Merchant ${merchant.id}`,
                    }))
              }
              disabled={loadingMerchants || merchants.length === 0}
              width={180}
              height={36}
            />
            <CustomSelector
              placeholder="All Status"
              value={statusFilter}
              onChange={(value: string) => setStatusFilter(value)}
              options={STATUS_FILTER_OPTIONS}
              width={150}
              height={36}
            />
            <CustomSelector
              placeholder="All Types"
              value={typeFilter}
              onChange={(value: string) => setTypeFilter(value)}
              options={TYPE_FILTER_OPTIONS}
              width={180}
              height={36}
            />
          </Space>
        </TopControlBar>
        <PromotionsTable
          promotions={paginatedPromotions}
          currentPage={currentPage}
          pageSize={pageSize}
          total={filteredPromotions.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onViewDetails={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
        onCopyCode={copyCode}
        onCreate={handleCreate}
        getStatusBadge={getStatusBadge}
        getTypeLabel={getTypeLabel}
        loading={loading}
      />
    </StyledCard>

      {/* Create/Edit Modal */}
      <PromotionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          dispatch(clearSelectedPromotion());
        }}
        selectedPromotion={selectedPromotion}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        onGenerateCode={generateCode}
        isLoading={loadingModal || loadingDetails}
        isSaving={saving}
      />

      {/* Details Modal */}
      <PromotionDetailsModal
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          dispatch(clearSelectedPromotion());
        }}
        promotion={selectedPromotion}
        onEdit={handleEdit}
        onCopyCode={copyCode}
        getStatusBadge={getStatusBadge}
        getTypeLabel={getTypeLabel}
        isSaving={saving}
      />
        </Container>
      </StyledThemeProvider>
    </ConfigProvider>
  );
};

export default Promotions;

